import { useState } from "react";

import { trackEvent } from "../../lib/analytics";
import { useI18n } from "../../lib/i18n";

// Choosing a beach is a group decision, and that link goes into a WhatsApp
// group whether we help or not. Right now it goes as a pasted URL we never see;
// with this it goes with the club's name attached and it shows up in GA4 next
// to the outbound clicks.
//
// navigator.share is the whole feature on a phone, which is where the group
// chat is. On a desktop it mostly does not exist, so the button falls back to
// copying and says so.
// navigator.clipboard rejects more often than you would expect: it needs a
// secure context, a live user gesture and a focused document, and it fails
// silently when any of those is missing. Measured this in the browser panel,
// where document.hasFocus() is false and the modern call rejects outright.
// execCommand is deprecated and works in exactly those cases, so it stays as
// the second attempt rather than as the only one.
async function copyToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard?.writeText(url);
    return true;
  } catch {
    // Fall through.
  }
  try {
    const field = document.createElement("textarea");
    field.value = url;
    // Off-screen rather than hidden: a display:none field cannot be selected.
    field.style.cssText = "position:fixed;top:-1000px;opacity:0";
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand("copy");
    field.remove();
    return ok;
  } catch {
    return false;
  }
}

export function ShareButton({ name, town }: { name: string; town: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = typeof window === "undefined" ? "" : window.location.href;
    const title = `${name}, ${town}`;
    const nav = typeof navigator === "undefined" ? undefined : navigator;

    if (nav?.share) {
      try {
        await nav.share({ title, text: title, url });
        trackEvent("share_club", { club: name, method: "native" });
        return;
      } catch {
        // A cancelled share sheet lands here as well as a genuine failure.
        // Either way, falling through to the clipboard leaves the visitor with
        // something rather than a button that appeared to do nothing.
      }
    }

    if (await copyToClipboard(url)) {
      setCopied(true);
      trackEvent("share_club", { club: name, method: "copy" });
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void share()}
      // aria-live so a screen reader hears the confirmation, which is the only
      // feedback this button gives.
      className="li-mono inline-flex min-h-9 items-center gap-1.5 rounded-full border border-[#1C2430]/20 px-3.5 py-1.5 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <circle cx="18" cy="5" r="2.6" />
        <circle cx="6" cy="12" r="2.6" />
        <circle cx="18" cy="19" r="2.6" />
        <path d="M8.3 10.8 15.7 6.4M8.3 13.2l7.4 4.4" strokeLinecap="round" />
      </svg>
      <span aria-live="polite">{copied ? t("share_copied") : t("share")}</span>
    </button>
  );
}
